/**
 * As opções de adiamento e o horário que cada uma significa.
 *
 * Função pura por causa da virada de dia: "amanhã de manhã" às 23h de terça é
 * quarta às 8h, e às 6h de terça também é quarta às 8h - não hoje às 8h, que já
 * passou. Errar isso esconde a mensagem por um dia inteiro a mais, ou a devolve
 * imediatamente. As duas falhas são silenciosas.
 */

export type QuandoAdiar = 'mais_tarde' | 'amanha' | 'fim_de_semana' | 'proxima_semana';

export interface OpcaoDeAdiamento {
  id: QuandoAdiar;
  rotulo: string;
}

/** Manhã e tarde na convenção do produto. */
const HORA_DA_MANHA = 8;
const HORA_DA_TARDE = 13;
/** "Mais tarde" é daqui a três horas, arredondado para a hora cheia. */
const HORAS_MAIS_TARDE = 3;

export const OPCOES_DE_ADIAMENTO: OpcaoDeAdiamento[] = [
  { id: 'mais_tarde', rotulo: 'Mais tarde hoje' },
  { id: 'amanha', rotulo: 'Amanhã de manhã' },
  { id: 'fim_de_semana', rotulo: 'Este fim de semana' },
  { id: 'proxima_semana', rotulo: 'Próxima semana' },
];

function comHora(base: Date, dias: number, hora: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + dias);
  d.setHours(hora, 0, 0, 0);
  return d;
}

/** O próximo dia da semana pedido, sempre no futuro (0 = domingo). */
function proximoDiaDaSemana(base: Date, diaDesejado: number, hora: number): Date {
  const faltam = (diaDesejado - base.getDay() + 7) % 7;
  // Zero significa "hoje": se a hora já passou, pula para a semana seguinte.
  const alvo = comHora(base, faltam, hora);
  return alvo.getTime() > base.getTime() ? alvo : comHora(base, faltam + 7, hora);
}

export function horarioDeAdiamento(quando: QuandoAdiar, agora = new Date()): Date {
  switch (quando) {
    case 'mais_tarde': {
      const d = new Date(agora);
      d.setHours(d.getHours() + HORAS_MAIS_TARDE, 0, 0, 0);
      return d;
    }

    case 'amanha':
      return comHora(agora, 1, HORA_DA_MANHA);

    case 'fim_de_semana':
      // Sábado. Já é sábado ou domingo? Então é o próximo.
      return proximoDiaDaSemana(agora, 6, HORA_DA_MANHA);

    case 'proxima_semana':
      // Segunda-feira que vem.
      return proximoDiaDaSemana(agora, 1, HORA_DA_MANHA);
  }
}

/** Como a data volta para o usuário na confirmação. */
export function descreverAdiamento(quando: Date): string {
  return quando.toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export { HORA_DA_MANHA, HORA_DA_TARDE };
